import { effect, inject, Injectable } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { getBrowserInfo } from '@awork/_shared/functions/browser-info'
import { getPixelRatio } from '@awork/_shared/functions/browser-infos'
import { AppQuery } from '@awork/core/state/app.query'
import { AppStore } from '@awork/core/state/app.store'
import { FileService } from '@awork/_shared/services/file-service/file.service'
import { SignalService } from '@awork/_shared/services/signal-service/signal.service'
import type {
  FailedRequestDiagnosticsEntry,
  HttpProbeResult,
  MemoryInfo,
  NavigatorConnection,
  NavigatorExtensions,
  RealtimeChannelDiagnostics,
  SupportDiagnosticsPayload
} from './types'
import { apiEndpoint } from '@awork/environments/environment'
import {
  catchError,
  endWith,
  filter,
  finalize,
  firstValueFrom,
  from,
  ignoreElements,
  map,
  type Observable,
  of,
  switchMap,
  take,
  timer
} from 'rxjs'
import { LiveEditingService } from '@awork/features/document/live-editing/live-editing.service'

@Injectable({ providedIn: 'root' })
export class SupportDiagnosticsService {
  private readonly appStore = inject(AppStore)
  private readonly appQuery = inject(AppQuery)
  private readonly fileService = inject(FileService)
  private readonly router = inject(Router)
  private readonly signalService = inject(SignalService)
  private readonly liveEditingService = inject(LiveEditingService)

  private wasLoggedIn = this.appQuery.getIsLoggedIn()
  private wasSwitchingWorkspace = this.appQuery.getIsSwitchingWorkspace()
  private isLoggedInSignal = this.appQuery.query('isLoggedIn')
  private isSwitchingWorkspaceSignal = this.appQuery.query('isSwitchingWorkspace')

  private readonly supportDiagnosticsFilename = 'support-diagnostics.json'
  private readonly maxFailedRequestEntries = 20
  private readonly supportRealtimeWarmupMs = 3000

  constructor() {
    effect(() => this.handleFailedRequestResetTriggers())
    this.checkForSupportSnapshotUpload()
  }

  /**
   * Adds a failed request diagnostics entry and keeps only the latest entries.
   * @param {FailedRequestDiagnosticsEntry} entry
   */
  addFailedRequest(entry: FailedRequestDiagnosticsEntry): void {
    const storedFailedRequests = this.appQuery.query('failedRequests')
    const failedRequests = [entry, ...storedFailedRequests()].slice(0, this.maxFailedRequestEntries)

    this.appStore.update({ failedRequests })
  }

  /**
   * Clears stored failed requests when the user logs out or a workspace switch starts.
   */
  private handleFailedRequestResetTriggers(): void {
    const isLoggedIn = this.isLoggedInSignal()
    const isSwitchingWorkspace = this.isSwitchingWorkspaceSignal()

    if (this.wasLoggedIn && !isLoggedIn) {
      this.appStore.update({ failedRequests: [] })
    }

    if (!this.wasSwitchingWorkspace && isSwitchingWorkspace) {
      this.appStore.update({ failedRequests: [] })
    }

    this.wasLoggedIn = isLoggedIn
    this.wasSwitchingWorkspace = isSwitchingWorkspace
  }

  /**
   * Creates a diagnostics JSON file with browser, network and realtime connection information.
   * @returns {Observable<File>}
   */
  getDiagnosticsFile(): Observable<File> {
    return from(this.buildDiagnosticsPayload()).pipe(
      map(payload => {
        const content = JSON.stringify(payload, null, 2)
        return new File([content], this.supportDiagnosticsFilename, { type: 'application/json' })
      })
    )
  }

  /**
   * Collects all diagnostics that can be gathered safely in the browser.
   * @returns {Promise<SupportDiagnosticsPayload>}
   */
  private async buildDiagnosticsPayload(): Promise<SupportDiagnosticsPayload> {
    const { name, version } = getBrowserInfo()
    const now = new Date()
    const nowMs = now.getTime()
    const navigatorWithExtensions = navigator as Navigator & NavigatorExtensions
    const connectionInfo = this.getNavigatorConnectionInfo(navigatorWithExtensions)
    const memoryInfo = (performance as Performance & { memory?: MemoryInfo }).memory
    const signalLastHeartbeatTimestamp = await firstValueFrom(this.signalService.lastHeartbeat$.pipe(take(1)))
    const signalHeartbeatAgeMs = signalLastHeartbeatTimestamp ? nowMs - signalLastHeartbeatTimestamp : null
    const signalDiagnostics = this.signalService.getRealtimeDiagnosticsSnapshot(nowMs)
    const liveEditingDiagnostics = this.liveEditingService.getRealtimeDiagnosticsSnapshot(nowMs)

    const appUrl = window.location.href
    const networkProbes = await this.collectNetworkProbes()
    const failedRequests = this.appQuery.query('failedRequests')
    const thirdPartyIntegrations = this.getThirdPartyIntegrationsDiagnostics()

    return {
      collectedAt: now.toISOString(),
      app: this.buildAppDiagnostics(appUrl),
      browser: this.buildBrowserDiagnostics(name, version),
      connection: this.buildConnectionDiagnostics(connectionInfo),
      memory: this.buildMemoryDiagnostics(navigatorWithExtensions, memoryInfo),
      system: this.buildSystemDiagnostics(),
      screen: this.buildScreenDiagnostics(),
      realtime: this.buildRealtimeDiagnostics(
        signalDiagnostics,
        liveEditingDiagnostics,
        signalLastHeartbeatTimestamp,
        signalHeartbeatAgeMs
      ),
      networkProbes,
      recentFailedRequests: failedRequests(),
      thirdPartyIntegrations
    }
  }

  /**
   * Checks for supportTicketId query param and triggers support files upload.
   */
  private checkForSupportSnapshotUpload(): void {
    this.router.events
      ?.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        take(1),
        map(() => this.router.parseUrl(this.router.url).queryParamMap.get('supportTicketId')),
        filter((supportTicketId): supportTicketId is string => !!supportTicketId),
        switchMap(supportTicketId =>
          timer(this.supportRealtimeWarmupMs).pipe(
            switchMap(() => this.fileService.getStoreDumpFile()),
            switchMap(storeDumpFile =>
              this.fileService
                .uploadStoreSnapshot(storeDumpFile, supportTicketId)
                .pipe(ignoreElements(), endWith(undefined))
            ),
            switchMap(() => this.uploadDiagnosticsBestEffort(supportTicketId)),
            map(() => {
              this.appStore.update({ failedRequests: [] })
              return undefined
            }),
            catchError(() => of(undefined)),
            finalize(() => this.clearSupportTicketIdQueryParam())
          )
        )
      )
      .subscribe()
  }

  /**
   * Uploads browser diagnostics for the support ticket without failing the snapshot upload flow.
   * @param {string} supportTicketId
   * @returns {Observable<void>}
   */
  private uploadDiagnosticsBestEffort(supportTicketId: string): Observable<void> {
    return this.getDiagnosticsFile().pipe(
      switchMap(diagnosticsFile => this.fileService.uploadSupportDiagnostics(diagnosticsFile, supportTicketId)),
      map(() => undefined),
      catchError(() => of(undefined))
    )
  }

  /**
   * Removes support ticket query parameter from the URL after the support upload flow ends.
   */
  private clearSupportTicketIdQueryParam(): void {
    this.router.navigate([], {
      queryParams: { supportTicketId: null },
      queryParamsHandling: 'merge'
    })
  }

  /**
   * Sends a lightweight request to check if an endpoint is reachable from the user browser.
   * @param {string} url
   * @returns {Promise<HttpProbeResult>}
   */
  private async probeHttpEndpoint(url: string): Promise<HttpProbeResult> {
    const startedAt = performance.now()
    const abortController = new AbortController()
    const timeoutId = window.setTimeout(() => abortController.abort(), 8000)

    try {
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'include',
        signal: abortController.signal
      })

      return {
        url,
        ok: response.ok,
        status: response.status,
        durationMs: Math.round(performance.now() - startedAt)
      }
    } catch (error: unknown) {
      return {
        url,
        ok: false,
        durationMs: Math.round(performance.now() - startedAt),
        error: error instanceof Error ? error.message : 'Request failed'
      }
    } finally {
      window.clearTimeout(timeoutId)
    }
  }

  /**
   * Returns the available navigator connection info for Chromium/Gecko/WebKit variants.
   * @param {Navigator & NavigatorExtensions} navigatorWithExtensions
   * @returns {NavigatorConnection | undefined}
   */
  private getNavigatorConnectionInfo(
    navigatorWithExtensions: Navigator & NavigatorExtensions
  ): NavigatorConnection | undefined {
    return (
      navigatorWithExtensions.connection ??
      navigatorWithExtensions.mozConnection ??
      navigatorWithExtensions.webkitConnection
    )
  }

  /**
   * Builds static app location diagnostics.
   * @param {string} appUrl
   * @returns {SupportDiagnosticsPayload['app']}
   */
  private buildAppDiagnostics(appUrl: string): SupportDiagnosticsPayload['app'] {
    return {
      url: appUrl,
      origin: window.location.origin,
      path: window.location.pathname,
      referrer: document.referrer
    }
  }

  /**
   * Builds browser diagnostics metadata.
   * @param {string} browserName
   * @param {string} browserVersion
   * @returns {SupportDiagnosticsPayload['browser']}
   */
  private buildBrowserDiagnostics(browserName: string, browserVersion: string): SupportDiagnosticsPayload['browser'] {
    return {
      name: browserName,
      version: browserVersion,
      userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      platform: navigator.platform,
      online: navigator.onLine,
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack ?? 'unspecified',
      visibilityState: document.visibilityState,
      colorDepth: screen.colorDepth
    }
  }

  /**
   * Builds network connection diagnostics from navigator connection APIs.
   * @param {NavigatorConnection | undefined} connectionInfo
   * @returns {SupportDiagnosticsPayload['connection']}
   */
  private buildConnectionDiagnostics(
    connectionInfo: NavigatorConnection | undefined
  ): SupportDiagnosticsPayload['connection'] {
    return {
      effectiveType: connectionInfo?.effectiveType ?? null,
      downlinkMbps: connectionInfo?.downlink ?? null,
      rttMs: connectionInfo?.rtt ?? null,
      saveData: connectionInfo?.saveData ?? null,
      type: connectionInfo?.type ?? null
    }
  }

  /**
   * Builds memory diagnostics from navigator and performance APIs.
   * @param {Navigator & NavigatorExtensions} navigatorWithExtensions
   * @param {MemoryInfo | undefined} memoryInfo
   * @returns {SupportDiagnosticsPayload['memory']}
   */
  private buildMemoryDiagnostics(
    navigatorWithExtensions: Navigator & NavigatorExtensions,
    memoryInfo: MemoryInfo | undefined
  ): SupportDiagnosticsPayload['memory'] {
    return {
      deviceMemoryGb: navigatorWithExtensions.deviceMemory ?? null,
      jsHeapSizeLimitBytes: memoryInfo?.jsHeapSizeLimit ?? null,
      totalJsHeapBytes: memoryInfo?.totalJSHeapSize ?? null,
      usedJsHeapBytes: memoryInfo?.usedJSHeapSize ?? null
    }
  }

  /**
   * Builds system capability diagnostics.
   * @returns {SupportDiagnosticsPayload['system']}
   */
  private buildSystemDiagnostics(): SupportDiagnosticsPayload['system'] {
    return {
      hardwareConcurrency: navigator.hardwareConcurrency ?? null,
      maxTouchPoints: navigator.maxTouchPoints ?? 0
    }
  }

  /**
   * Builds viewport and screen diagnostics.
   * @returns {SupportDiagnosticsPayload['screen']}
   */
  private buildScreenDiagnostics(): SupportDiagnosticsPayload['screen'] {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      devicePixelRatio: window.devicePixelRatio ?? 1,
      pixelRatio: getPixelRatio()
    }
  }

  /**
   * Builds realtime diagnostics from websocket and live-editing channels.
   * @param {ReturnType<SignalService['getRealtimeDiagnosticsSnapshot']>} signalDiagnostics
   * @param {RealtimeChannelDiagnostics | null} liveEditingDiagnostics
   * @param {number | null} signalLastHeartbeatTimestamp
   * @param {number | null} signalHeartbeatAgeMs
   * @returns {SupportDiagnosticsPayload['realtime']}
   */
  private buildRealtimeDiagnostics(
    signalDiagnostics: ReturnType<SignalService['getRealtimeDiagnosticsSnapshot']>,
    liveEditingDiagnostics: RealtimeChannelDiagnostics | null,
    signalLastHeartbeatTimestamp: number | null,
    signalHeartbeatAgeMs: number | null
  ): SupportDiagnosticsPayload['realtime'] {
    return {
      channels: {
        notifications: {
          state: signalDiagnostics.state,
          host: signalDiagnostics.host,
          hubPath: signalDiagnostics.hubPath,
          connectionId: signalDiagnostics.connectionId,
          reconnectCount: signalDiagnostics.reconnectCount,
          lastError: signalDiagnostics.lastError,
          lastHeartbeatAt: signalDiagnostics.lastHeartbeatAt,
          heartbeatAgeMs: signalDiagnostics.heartbeatAgeMs
        },
        liveEditing: liveEditingDiagnostics
      },
      signalConnectionId: this.signalService.connectionId ?? null,
      signalLastHeartbeatAt: signalLastHeartbeatTimestamp ? new Date(signalLastHeartbeatTimestamp).toISOString() : null,
      signalHeartbeatAgeMs
    }
  }

  /**
   * Collects probe results for key web and backend endpoints.
   * @returns {Promise<HttpProbeResult[]>}
   */
  private async collectNetworkProbes(): Promise<HttpProbeResult[]> {
    const appProbeUrl = `${window.location.origin}/?supportProbe=true`
    const apiProbeUrl = new URL(apiEndpoint, window.location.origin).toString()
    const coreServiceStatusProbeUrl = this.getCoreServiceStatusProbeUrl()
    const cdnProbeUrl = this.getCdnProbeUrl()

    return Promise.all([
      this.probeHttpEndpoint(appProbeUrl),
      this.probeHttpEndpoint(apiProbeUrl),
      this.probeHttpEndpoint(coreServiceStatusProbeUrl),
      this.probeHttpEndpoint(cdnProbeUrl)
    ])
  }

  /**
   * Returns the detected status of third-party support integrations loaded via scripts.
   * @returns {{ intercom: { scriptTagDetected: boolean; globalAvailable: boolean; }; birdie: { scriptTagDetected: boolean; globalAvailable: boolean; }; }}
   */
  private getThirdPartyIntegrationsDiagnostics(): {
    intercom: {
      scriptTagDetected: boolean
      globalAvailable: boolean
    }
    birdie: {
      scriptTagDetected: boolean
      globalAvailable: boolean
    }
  } {
    const scriptSources = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]')).map(script =>
      script.src.toLowerCase()
    )
    const win = window as Window & { Intercom?: unknown; birdie?: unknown }

    return {
      intercom: {
        scriptTagDetected: scriptSources.some(source => source.includes('intercom') || source.includes('segment')),
        globalAvailable: typeof win.Intercom === 'function'
      },
      birdie: {
        scriptTagDetected: scriptSources.some(source => source.includes('birdie')),
        globalAvailable: typeof win.birdie !== 'undefined'
      }
    }
  }

  /**
   * Returns the core service status probe URL on the corresponding app domain.
   * Example: api.cwork.io -> app.cwork.io, api.awork.com -> app.awork.com
   * @returns {string}
   */
  private getCoreServiceStatusProbeUrl(): string {
    try {
      const apiUrl = new URL(apiEndpoint, window.location.origin)
      const appHost = apiUrl.host.replace(/^api\./i, 'app.')

      return `${apiUrl.protocol}//${appHost}/api/v1/coreservicestatus`
    } catch {
      return `${window.location.origin}/api/v1/coreservicestatus`
    }
  }

  /**
   * Returns a stable CDN asset URL to verify cdn.awork.com reachability.
   * @returns {string}
   */
  private getCdnProbeUrl(): string {
    return 'https://cdn.awork.com/app/prod/assets/images/visuals/logo.svg'
  }
}
